import React, { useEffect, useRef, useState } from 'react';
import { Bell } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useNotifications } from '../../hooks/useNotifications';
import {
  AppNotification,
  NotificationPersona,
} from '../../services/notifications/notificationService';
import { toInAppNotificationPath } from '../../utils/notificationDeepLink';

type NotificationsBellProps = {
  persona: NotificationPersona;
  centerPath: string;
  /** `sidebar` sits in the utility strip; opens the preview panel upward. */
  variant?: 'default' | 'sidebar';
  /** When true with sidebar variant, open the panel to the right of the collapsed rail. */
  sidebarCollapsed?: boolean;
  /** Highlight when the notifications center route is active. */
  active?: boolean;
};

const NotificationsBell: React.FC<NotificationsBellProps> = ({
  persona,
  centerPath,
  variant = 'default',
  sidebarCollapsed = false,
  active = false,
}) => {
  const navigate = useNavigate();
  const { items, unreadCount, markRead } = useNotifications(persona);
  const [open, setOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const previewItems = items.slice(0, 5);
  const isSidebar = variant === 'sidebar';

  useEffect(() => {
    if (!open) return;

    const closeOnOutsideClick = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) setOpen(false);
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };

    document.addEventListener('mousedown', closeOnOutsideClick);
    document.addEventListener('keydown', closeOnEscape);
    return () => {
      document.removeEventListener('mousedown', closeOnOutsideClick);
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, [open]);

  const openNotification = (notification: AppNotification) => {
    if (notification.status === 'unread') {
      void markRead(notification.id);
    }
    setOpen(false);

    const target = toInAppNotificationPath(notification.deepLink);
    if (target) {
      navigate(target);
      return;
    }
    // FYI with no deep link: stay put after mark-read (preview already showed the copy).
  };

  return (
    <div ref={menuRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((current) => !current)}
        aria-label={`Notifications${unreadCount ? `, ${unreadCount} unread` : ''}`}
        aria-expanded={open}
        aria-haspopup="true"
        aria-current={active ? 'page' : undefined}
        className={
          isSidebar
            ? active
              ? 'relative inline-flex h-9 w-9 items-center justify-center rounded-lg bg-gray-100 text-accent transition-colors'
              : 'relative inline-flex h-9 w-9 items-center justify-center rounded-lg text-gray-600 transition-colors hover:bg-gray-100 hover:text-gray-900'
            : 'relative inline-flex h-10 w-10 items-center justify-center rounded-md border border-gray-300 bg-white text-gray-600 transition-colors hover:bg-gray-50'
        }
      >
        <Bell className="h-5 w-5" aria-hidden />
        {unreadCount > 0 && (
          <span className="absolute -right-1 -top-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-accent px-1 text-[11px] font-semibold text-accent-foreground">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div
          className={
            isSidebar && sidebarCollapsed
              ? 'fixed bottom-24 left-16 z-50 w-80 overflow-hidden rounded-lg border border-gray-200 bg-white shadow-lg sm:w-96'
              : isSidebar
                ? 'absolute bottom-full left-0 z-50 mb-2 w-80 overflow-hidden rounded-lg border border-gray-200 bg-white shadow-lg sm:w-96'
                : 'absolute right-0 z-50 mt-2 w-80 overflow-hidden rounded-lg border border-gray-200 bg-white shadow-lg sm:w-96'
          }
        >
          <div className="border-b border-gray-100 px-4 py-3">
            <p className="text-sm font-semibold text-gray-900">Notifications</p>
            <p className="mt-0.5 text-xs text-gray-500">
              {unreadCount ? `${unreadCount} unread` : 'You are all caught up'}
            </p>
          </div>
          {previewItems.length ? (
            <ul className="max-h-72 overflow-y-auto divide-y divide-gray-100">
              {previewItems.map((notification) => {
                const unread = notification.status === 'unread';
                return (
                  <li key={notification.id} className={unread ? 'bg-blue-50/40' : undefined}>
                    <button
                      type="button"
                      onClick={() => void openNotification(notification)}
                      className="flex w-full items-start gap-3 px-4 py-3 text-left hover:bg-gray-50"
                    >
                      <span
                        className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${
                          unread ? 'bg-accent' : 'bg-transparent'
                        }`}
                        aria-hidden
                      />
                      <span className="min-w-0 flex-1">
                        <span
                          className={`block truncate text-sm ${
                            unread ? 'font-semibold text-gray-900' : 'font-medium text-gray-800'
                          }`}
                        >
                          {notification.title}
                        </span>
                        <span className="mt-0.5 block truncate text-xs text-gray-500">
                          {notification.body}
                        </span>
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          ) : (
            <p className="px-4 py-6 text-center text-sm text-gray-500">No notifications yet.</p>
          )}
          <div className="border-t border-gray-100 bg-gray-50 px-4 py-2.5">
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                navigate(centerPath);
              }}
              className="block w-full text-center text-sm font-semibold text-accent hover:text-rowing-navy"
            >
              See all notifications
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationsBell;
